/**
 * rewards-bar.js
 * Live cart-rewards progress bar for the Pialu Cornerstone theme.
 *
 * - Reads tier config from data-* attributes (no inline JSON).
 * - Initial state comes from Liquid; this script re-fetches /cart.js on load.
 * - Listens broadly for cart events and (as a fallback) wraps window.fetch
 *   to re-read /cart.js after any POST to /cart/add|change|update.
 * - Dependency-free, idempotent, respects prefers-reduced-motion implicitly
 *   (the CSS handles transition disabling).
 *
 * Lot B4 (09.2026) : deux modes, choisis par data-oferta sur <rewards-progress-bar>.
 * - 'local' (défaut) : chemin d'origine, inchangé (renderLocal : item_count / total_price
 *   contre les paliers des blocs).
 * - 'global' : la bibliothèque commune window.PialuOferta (assets/pialu-oferta.js) calcule
 *   l'état depuis le panier /cart.js entier (remises réellement appliquées) ; renderGlobal
 *   applique la vue pure computeBarView. Bibliothèque absente ou JSON cassé → l'état rendu
 *   par le serveur reste tel quel (mode dégradé, principe 10 du plan).
 */
(function () {
  'use strict';

  if (window.__rewardsBarInit) {
    // Already initialized once: just (re)bind any new elements below.
  }

  var CART_EVENTS = [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'quick-cart:updated',
    'ajaxCart:afterCartLoad',
  ];

  /* ---------------------------------------------------------------------- */
  /* Money formatting (mirrors Shopify's money_format placeholders)         */
  /* ---------------------------------------------------------------------- */

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '{{amount}}';

    function defaultTo(num) {
      return num || 0;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision === 0 ? 0 : precision || 2);
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands
      );
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    var amount = '';
    var match = fmt.match(placeholderRegex);
    var placeholder = match ? match[1] : 'amount';

    switch (placeholder) {
      case 'amount':
        amount = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        amount = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        amount = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        amount = formatWithDelimiters(cents, 2);
    }

    return fmt.replace(placeholderRegex, amount);
  }

  /* ---------------------------------------------------------------------- */
  /* Tier parsing + rendering                                               */
  /* ---------------------------------------------------------------------- */

  function parseTiers(root, mode) {
    var nodes = root.querySelectorAll('.rewards-progress__tier-config');
    var tiers = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var raw = parseFloat(n.getAttribute('data-threshold'));
      if (isNaN(raw)) continue;
      // In price mode, thresholds are entered in major currency units → cents.
      var threshold = mode === 'price' ? Math.round(raw * 100) : raw;
      tiers.push({
        threshold: threshold,
        rawThreshold: raw,
        pending: n.getAttribute('data-message') || '',
        reached: n.getAttribute('data-reached') || '',
      });
    }
    tiers.sort(function (a, b) {
      return a.threshold - b.threshold;
    });
    return tiers;
  }

  function buildMessage(template, remaining, thresholdRaw, mode, moneyFormat) {
    var remainingDisplay;
    var thresholdDisplay;
    if (mode === 'price') {
      remainingDisplay = formatMoney(remaining, moneyFormat);
      thresholdDisplay = formatMoney(thresholdRaw * 100, moneyFormat);
    } else {
      remainingDisplay = String(remaining);
      thresholdDisplay = String(thresholdRaw);
    }
    return String(template)
      .replace(/\[remaining\]/g, remainingDisplay)
      .replace(/\[threshold\]/g, thresholdDisplay)
      .replace(/\{\{\s*remaining\s*\}\}/g, remainingDisplay)
      .replace(/\{\{\s*threshold\s*\}\}/g, thresholdDisplay);
  }

  /* Corrección #4 — objetivo único: compra N joyas, llévate 1 gratis.
     Mensajes por número de artículos (0/1/2/>=N). */
  function renderSingle(root, count) {
    var required = parseInt(root.getAttribute('data-required-qty'), 10) || 3;
    var m0 = root.getAttribute('data-msg-0') || '';
    var m1 = root.getAttribute('data-msg-1') || '';
    var m2 = root.getAttribute('data-msg-2') || '';
    var mDone = root.getAttribute('data-msg-done') || '';

    var percent = required > 0 ? (count / required) * 100 : 0;
    if (percent > 100) percent = 100;
    if (percent < 0) percent = 0;

    var fillEl = root.querySelector('.rewards-progress__fill');
    if (fillEl) fillEl.style.width = percent + '%';
    var trackEl = root.querySelector('.rewards-progress__track');
    if (trackEl) trackEl.setAttribute('aria-valuenow', String(Math.round(percent)));

    var tick = root.querySelector('.rewards-progress__tick');
    if (tick) {
      if (count >= required) tick.classList.add('is-reached');
      else tick.classList.remove('is-reached');
    }

    var text;
    if (count >= required) text = mDone;
    else if (count === 1) text = m1;
    else if (count === 2) text = m2;
    else text = m0;

    var msgEl = root.querySelector('.rewards-progress__message-text');
    if (msgEl) msgEl.textContent = text;
  }

  function renderLocal(root, currentValue) {
    if (root.getAttribute('data-goal-mode') === 'single') {
      renderSingle(root, currentValue);
      return;
    }
    var mode = root.getAttribute('data-mode') === 'price' ? 'price' : 'count';
    var moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';
    var completedMsg = root.getAttribute('data-completed-message') || '';
    var emptyMsg = root.getAttribute('data-empty-message') || '';

    var tiers = parseTiers(root, mode);
    if (!tiers.length) return;

    var finalThreshold =
      parseFloat(root.getAttribute('data-final-threshold')) ||
      tiers[tiers.length - 1].threshold;

    /* --- fill % --- */
    var percent = 0;
    if (finalThreshold > 0) {
      percent = (currentValue / finalThreshold) * 100;
    }
    if (percent > 100) percent = 100;
    if (percent < 0) percent = 0;

    var fillEl = root.querySelector('.rewards-progress__fill');
    if (fillEl) fillEl.style.width = percent + '%';

    var trackEl = root.querySelector('.rewards-progress__track');
    if (trackEl) trackEl.setAttribute('aria-valuenow', String(Math.round(percent)));

    /* --- tick reached states --- */
    var tickEls = root.querySelectorAll('.rewards-progress__tick');
    // Ticks are rendered in source (unsorted) order matching the tier-config
    // nodes, so re-read thresholds from the adjacent config nodes.
    var configNodes = root.querySelectorAll('.rewards-progress__tier-config');
    for (var t = 0; t < tickEls.length; t++) {
      var cfg = configNodes[t];
      if (!cfg) continue;
      var raw = parseFloat(cfg.getAttribute('data-threshold'));
      var thr = mode === 'price' ? Math.round(raw * 100) : raw;
      if (currentValue >= thr) {
        tickEls[t].classList.add('is-reached');
      } else {
        tickEls[t].classList.remove('is-reached');
      }
    }

    /* --- message --- */
    var nextTier = null;
    var lastReached = null;
    for (var i = 0; i < tiers.length; i++) {
      if (currentValue >= tiers[i].threshold) {
        lastReached = tiers[i];
      } else if (nextTier === null) {
        nextTier = tiers[i];
      }
    }

    var text = '';
    if (nextTier) {
      var remaining = nextTier.threshold - currentValue;
      if (remaining < 0) remaining = 0;
      var pending = buildMessage(
        nextTier.pending,
        remaining,
        nextTier.rawThreshold,
        mode,
        moneyFormat
      );
      if (lastReached && lastReached.reached) {
        text = lastReached.reached + '. ' + pending;
      } else if (currentValue <= 0 && emptyMsg) {
        text = emptyMsg;
      } else {
        text = pending;
      }
    } else {
      // All tiers reached.
      text = completedMsg || (lastReached ? lastReached.reached : '');
    }

    var msgEl = root.querySelector('.rewards-progress__message-text');
    if (msgEl) msgEl.textContent = text;
  }

  /* ---------------------------------------------------------------------- */
  /* Lot B4 : point d'entrée commun + mode global (Pialú · Oferta)          */
  /* ---------------------------------------------------------------------- */

  /* Reçoit le panier /cart.js entier. Mode local : on en tire la valeur comme avant. */
  function render(root, cart) {
    if (root.getAttribute('data-oferta') === 'global') {
      try {
        renderGlobal(root, cart);
      } catch (e) {
        /* jamais d'exception vers l'enveloppe fetch : l'état serveur reste */
      }
      return;
    }
    var mode = root.getAttribute('data-mode') === 'price' ? 'price' : 'count';
    renderLocal(root, currentValueFromCart(cart, mode));
  }

  function renderGlobal(root, cart) {
    var OF = window.PialuOferta;
    var cfg = OF && OF.cfg();
    if (!cfg) return; // bibliothèque absente ou JSON cassé : l'état serveur (exact) reste affiché
    if (cfg.coleccionTronquee) {
      // Collection > 50 produits : on attend la liste complète ; rien n'est redessiné en attendant
      // (l'état serveur est exact, comme après une erreur réseau). ids null = échec → on garde.
      OF.loadIds(cfg.coleccion).then(function (ids) {
        if (ids) drawGlobal(root, OF.computeBarView(OF.state(cart, cfg, ids), cfg));
      }).catch(function () { /* jamais de rejet non géré : l'état serveur reste */ });
      return;
    }
    drawGlobal(root, OF.computeBarView(OF.state(cart, cfg, null), cfg));
  }

  /* Applique une vue pure (computeBarView) au DOM : pastilles, remplissage, message, phase, silence.
     Mêmes gabarits que le Liquid de la section → aucun changement visible au premier /cart.js. */
  function drawGlobal(root, view) {
    if (!view || view.keep) return;

    var fillEl = root.querySelector('.rewards-progress__fill');
    if (fillEl) fillEl.style.width = view.fill + '%';
    var trackEl = root.querySelector('.rewards-progress__track');
    if (trackEl) trackEl.setAttribute('aria-valuenow', String(view.fill));

    var tickEls = root.querySelectorAll('.rewards-progress__tick');
    for (var t = 0; t < tickEls.length; t++) {
      var tick = view.ticks[t];
      if (!tick) continue;
      tickEls[t].classList.toggle('is-reached', tick.allumee);
      var txt = tickEls[t].querySelector('.pialu-rw__chip-text');
      if (txt && txt.textContent !== tick.texto) txt.textContent = tick.texto;
    }

    var msgEl = root.querySelector('.rewards-progress__message-text');
    // Écrit seulement si le texte change : aria-live="polite" ne relit pas un message identique.
    if (msgEl && msgEl.textContent !== view.message) msgEl.textContent = view.message;

    root.setAttribute('data-oferta-phase', view.phase);
    var wrap = root.closest('.pialu-rewards');
    if (wrap) wrap.classList.toggle('is-silent', view.silent); // à chaque rendu (ré-ajout après suppression)
  }

  /* ---------------------------------------------------------------------- */
  /* Cart fetching                                                          */
  /* ---------------------------------------------------------------------- */

  var roots = [];
  var refetchTimer = null;

  function currentValueFromCart(cart, mode) {
    if (!cart) return 0;
    return mode === 'price' ? cart.total_price || 0 : cart.item_count || 0;
  }

  function refreshAll() {
    if (!roots.length) return;
    fetch(window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;
        for (var i = 0; i < roots.length; i++) {
          render(roots[i], cart); // panier entier ; le mode local en tire item_count / total_price
        }
      })
      .catch(function () {
        /* network error: keep server-rendered state */
      });
  }

  function scheduleRefresh() {
    if (refetchTimer) clearTimeout(refetchTimer);
    refetchTimer = setTimeout(refreshAll, 120);
  }

  /* ---------------------------------------------------------------------- */
  /* fetch() wrapper fallback (idempotent)                                  */
  /* ---------------------------------------------------------------------- */

  function installFetchWrapper() {
    if (window.__rewardsBarFetchWrapped) return;
    if (typeof window.fetch !== 'function') return;
    window.__rewardsBarFetchWrapped = true;

    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) {
        url = '';
      }
      var promise = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        promise
          .then(function (resp) {
            scheduleRefresh();
            return resp;
          })
          .catch(function () {});
      }
      return promise;
    };
  }

  /* ---------------------------------------------------------------------- */
  /* Init                                                                   */
  /* ---------------------------------------------------------------------- */

  function collectRoots() {
    var found = document.querySelectorAll('rewards-progress-bar');
    roots = [];
    for (var i = 0; i < found.length; i++) {
      roots.push(found[i]);
      var el = found[i];
      // Mode global : le Liquid a tout dessiné (vérité du panier) ; on attend /cart.js.
      if (el.getAttribute('data-oferta') === 'global') continue;
      // Render once immediately from the server-provided initial value.
      var mode = el.getAttribute('data-mode') === 'price' ? 'price' : 'count';
      var initial = parseFloat(el.getAttribute('data-initial-value')) || 0;
      renderLocal(el, initial);
    }
  }

  /* Corrección #4/#6 — fijar la barra respecto al header, según su posición.
     Encuentra el .shopify-section del header (z-index 510 en Cornerstone). */
  function getHeaderWrap() {
    var w = document.querySelector('.shopify-section.header__outer-wrapper');
    if (w) return w;
    var hdr = document.querySelector('.header__outer-wrapper') ||
              document.querySelector('.header') ||
              document.getElementById('header');
    return hdr ? (hdr.closest('.shopify-section') || hdr) : null;
  }
  function headerHeight(headerWrap) {
    var hw = headerWrap || getHeaderWrap();
    return hw ? hw.offsetHeight : 0;
  }
  /* Corrección #6 — el header del tema tiene z-index 510 y sticky top:0. Si la barra
     se coloca ENCIMA del header, antes el offset de altura la metía dentro del header y
     este la tapaba "a medias". Ahora detectamos la posición:
       - debajo del header  → top = altura del header (comportamiento validado, z-index 30 < 510)
       - encima del header  → top = 0, z-index 511 (por encima) y empujamos el header sticky
                              hacia abajo (su top = altura de la barra) para que no se solapen. */
  function applyStickyOffset() {
    var headerWrap = getHeaderWrap();
    var stickyHeaderOn = document.documentElement.classList.contains('sticky-header-enabled');

    // Restaura cualquier desplazamiento que hayamos aplicado antes al header
    // (p. ej. si el comerciante vuelve a mover la barra debajo del header).
    if (headerWrap && headerWrap.getAttribute('data-pialu-pushed') === '1') {
      headerWrap.style.top = '';
      headerWrap.removeAttribute('data-pialu-pushed');
    }

    var bars = document.querySelectorAll('[data-rewards-sticky="true"]');
    if (!bars.length) return;

    var h = headerHeight(headerWrap);

    for (var i = 0; i < bars.length; i++) {
      var off = parseInt(bars[i].getAttribute('data-rewards-offset'), 10) || 0;
      var wrap = bars[i].closest('.shopify-section') || bars[i];
      wrap.classList.add('pialu-sticky');

      var above = false;
      if (headerWrap && wrap !== headerWrap) {
        // La barra está ENCIMA del header si el header aparece DESPUÉS en el documento.
        above = !!(wrap.compareDocumentPosition(headerWrap) & 4 /* DOCUMENT_POSITION_FOLLOWING */);
      }

      if (above) {
        wrap.style.setProperty('--rewards-sticky-top', off + 'px');
        wrap.style.zIndex = '511';
        if (headerWrap && stickyHeaderOn) {
          headerWrap.style.top = (wrap.offsetHeight + off) + 'px';
          headerWrap.setAttribute('data-pialu-pushed', '1');
        }
      } else {
        wrap.style.setProperty('--rewards-sticky-top', ((h || 0) + off) + 'px');
        wrap.style.zIndex = '';
      }
    }
  }

  /* Lot B4 : la hauteur de la barre change (message sur 2 lignes, pastilles à la ligne à 360 px) ;
     le header sticky doit être repoussé de la hauteur mesurée, pas seulement à resize/load. */
  function observeBarHeight() {
    if (typeof ResizeObserver !== 'function') return;
    var bars = document.querySelectorAll('[data-rewards-sticky="true"]');
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].__pialuResizeObserved) continue;
      bars[i].__pialuResizeObserved = true;
      new ResizeObserver(function () {
        applyStickyOffset();
      }).observe(bars[i]);
    }
  }

  function init() {
    collectRoots();
    if (!roots.length) return;

    installFetchWrapper();
    applyStickyOffset();
    observeBarHeight();

    // Re-fetch the live cart on load (covers cached/persisted carts).
    scheduleRefresh();

    if (!window.__rewardsBarEventsBound) {
      window.__rewardsBarEventsBound = true;
      CART_EVENTS.forEach(function (name) {
        document.addEventListener(name, scheduleRefresh);
      });
      // Recalcular el offset del header al redimensionar / cargar.
      window.addEventListener('resize', applyStickyOffset);
      window.addEventListener('load', applyStickyOffset);
      // Some themes fire a 'shopify:section:load' in the editor.
      document.addEventListener('shopify:section:load', function () {
        collectRoots();
        applyStickyOffset();
        observeBarHeight();
        scheduleRefresh();
      });
      // Re-read when the page becomes visible again.
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') scheduleRefresh();
      });
    }

    window.__rewardsBarInit = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
